import { InputAdornment, TextField } from "@mui/material";
import {
  Check as CheckIcon,
  Columns3 as ColumnsIcon,
  Search as SearchIcon,
  X as XIcon,
} from "lucide-react";
import * as React from "react";

import { Popover } from "@/shared/foundation/primitives/Popover";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";

import type { ColumnPickerOption } from "../columnVisibility";

import styles from "./ColumnVisibilityPicker.module.css";

export interface ColumnVisibilityPickerProps {
  /**
   * Column options the user can toggle. Order is preserved as given.
   */
  options: ColumnPickerOption[];

  /**
   * Set of column ids currently visible in the table. Membership controls
   * each row's checked state.
   */
  visibleColumnIds: Set<string>;

  /**
   * Command-style setter invoked when the user clicks a row. The picker
   * passes the desired next visibility — derived from the row's current
   * checked state — so the implementation can skip a read-back round-trip.
   */
  setColumnVisibility: (id: string, visible: boolean) => void;
}

/**
 * "Columns" button + popover that toggles which columns are visible in the
 * companion `<DataTable>`. The button shows a count badge of currently
 * visible columns; the popover lists every available column as a
 * checkbox-style row that the user can click to add / remove from the
 * visible set.
 *
 * State is held outside this component — `visibleColumnIds` is the source
 * of truth, and `setColumnVisibility` flows individual toggles back to the
 * consumer.
 */
export function ColumnVisibilityPicker({
  options,
  visibleColumnIds,
  setColumnVisibility,
}: ColumnVisibilityPickerProps) {
  return (
    <Popover>
      <Popover.Trigger
        render={
          <RobotoButton
            variant="secondary"
            className={styles.trigger}
            // Leading icon so the button collapses to icon-only alongside Filter
            // when the toolbar opts into --button-label-display on small screens.
            icon={<ColumnsIcon aria-hidden="true" />}
            aria-label="Columns"
          >
            Columns
            <span className={styles.triggerCount} aria-hidden="true">
              {visibleColumnIds.size}
            </span>
          </RobotoButton>
        }
      />
      <Popover.Content aria-label="Choose columns" className={styles.popover}>
        <PickerBody
          options={options}
          visibleColumnIds={visibleColumnIds}
          setColumnVisibility={setColumnVisibility}
        />
      </Popover.Content>
    </Popover>
  );
}

type PickerScope = "all" | "selected";

function PickerBody({
  options,
  visibleColumnIds,
  setColumnVisibility,
}: ColumnVisibilityPickerProps) {
  const [search, setSearch] = React.useState("");
  const [scope, setScope] = React.useState<PickerScope>("all");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const needle = search.trim().toLowerCase();

  // Scope narrows the candidate set first; search filters within scope.
  const filtered = React.useMemo(() => {
    const scoped =
      scope === "selected"
        ? options.filter((o) => visibleColumnIds.has(o.id))
        : options;
    if (!needle) {
      return scoped;
    }
    return scoped.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        o.id.toLowerCase().includes(needle),
    );
  }, [options, scope, visibleColumnIds, needle]);

  const clearSearch = () => {
    setSearch("");
    inputRef.current?.focus();
  };

  return (
    <>
      <div className={styles.search}>
        <TextField
          autoComplete="off"
          inputRef={inputRef}
          size="small"
          type="search"
          className={styles.searchInput}
          placeholder="Search columns..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          inputProps={{ "aria-label": "Search columns" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={14} />
              </InputAdornment>
            ),
            endAdornment:
              search.length > 0 ? (
                <InputAdornment position="end">
                  <button
                    type="button"
                    className={styles.searchClear}
                    onClick={clearSearch}
                    aria-label="Clear search"
                  >
                    <XIcon size={14} />
                  </button>
                </InputAdornment>
              ) : null,
          }}
        />
      </div>
      <div className={styles.tabs} role="tablist" aria-label="Filter columns">
        <ScopeTab
          label="All"
          count={options.length}
          active={scope === "all"}
          onSelect={() => setScope("all")}
        />
        <ScopeTab
          label="Selected"
          count={visibleColumnIds.size}
          active={scope === "selected"}
          onSelect={() => setScope("selected")}
        />
      </div>
      {filtered.length === 0 ? (
        <div className={styles.empty}>
          {emptyMessage(needle, search, scope)}
        </div>
      ) : (
        <div className={styles.list} role="listbox" aria-multiselectable>
          {filtered.map((option) => {
            const checked = visibleColumnIds.has(option.id);
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                className={styles.option}
                data-checked={checked ? "true" : "false"}
                aria-checked={checked}
                onClick={() => setColumnVisibility(option.id, !checked)}
              >
                <span className={styles.optionCheck} aria-hidden="true">
                  {checked ? <CheckIcon size={11} strokeWidth={3} /> : null}
                </span>
                <span className={styles.optionLabel}>
                  <Highlight text={option.label} needle={needle} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

function ScopeTab({
  label,
  count,
  active,
  onSelect,
}: {
  label: string;
  count: number;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-active={active ? "true" : "false"}
      className={styles.tabButton}
      onClick={onSelect}
    >
      {label}
      <span className={styles.tabCount} aria-hidden="true">
        {count}
      </span>
    </button>
  );
}

function emptyMessage(
  needle: string,
  search: string,
  scope: PickerScope,
): string {
  if (needle.length > 0) {
    return `No columns match "${search.trim()}"`;
  }
  if (scope === "selected") {
    return "No columns selected";
  }
  return "No columns available";
}

/**
 * Wraps a substring match in `<mark>`. Case-insensitive; first match only.
 */
function Highlight({ text, needle }: { text: string; needle: string }) {
  if (!needle) {
    return <>{text}</>;
  }
  const idx = text.toLowerCase().indexOf(needle);
  if (idx < 0) {
    return <>{text}</>;
  }
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + needle.length)}</mark>
      {text.slice(idx + needle.length)}
    </>
  );
}
